import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import { toast } from 'react-toastify';
import { RootThunkAPI } from 'app/store';
import {
  IAddParticipantsToCompetitions,
  IChangeCompetition,
  ICreateNewCompetition,
  MChangeCompetitionStatus,
} from './types';
import { mainServiceWithCredentials } from '../../shared/services/http';
import { competitionsB2CRApi } from './api';

type RootThunkApiWithReject = RootThunkAPI & {
  rejectWithValue: (value: unknown) => unknown;
};

const handleErrorThunk = (e: any, thunkAPI: RootThunkApiWithReject, isCloseDrawer = false) => {
  thunkAPI.rejectWithValue(e);
  const { data, status } = e.response;
  const { detail } = data;

  if (status === 500) {
    toast.error('Возникла непредвиденная ошибка. Пожалуйста, попробуйте еще раз.');
  }

  if (isCloseDrawer) {
    thunkAPI.dispatch(setDrawerOpen(false));
  }

  return { detail, status };
};

const createNewCompetitionThunk = createAsyncThunk(
  'competitionsApi/createNewCompetitionThunk',
  async (form: ICreateNewCompetition.Params, thunkAPI) => {
    try {
      const body = new FormData();
      body.append('app_type', 'b2cr');

      (Object.keys(form) as Array<keyof ICreateNewCompetition.Params>).forEach(
        // @ts-ignore
        (key) => form[key] !== undefined && key !== 'status' && key !== 'kpi_name_ids' && key !== 'brand_ids' && body.append(key, form[key]),
      );
      form.kpi_name_ids.forEach((item) => {
        body.append('kpi_name_ids', item.toString());
      });
      form.brand_ids.forEach((item) => {
        body.append('brand_ids', item.toString());
      });

      const data: AxiosResponse<ICreateNewCompetition.Response> = await mainServiceWithCredentials.post(
        'competitions/admin/competition/',
        body,
      );

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2CRApi.util.invalidateTags(['allCompetitionsB2CR']));
      toast.success('Соревнование создано');

      return data.data;
    } catch (e: any) {
      return handleErrorThunk(e, thunkAPI as RootThunkApiWithReject);
    }
  },
);

const updateCompetitionThunk = createAsyncThunk(
  'competitionsApi/updateCompetitionsThunk',
  async (form: IChangeCompetition.Params, thunkAPI) => {
    try {
      const body = new FormData();
      body.append('app_type', 'b2cr');

      (Object.keys(form) as Array<keyof IChangeCompetition.Params>).forEach(
        // @ts-ignore
        (key) =>
          form[key] !== undefined &&
          // @ts-ignore
          key !== 'status' &&
          key !== 'kpi_name_ids' &&
          key !== 'brand_ids' &&
          key !== 'image_main' &&
          // @ts-ignore
          body.append(key, form[key]),
      );

      if (typeof form.image_main !== 'string') {
        body.append('image_main', form.image_main);
      }
      form.kpi_name_ids.forEach((item) => {
        body.append('kpi_name_ids', item.toString());
      });
      form.brand_ids.forEach((item) => {
        body.append('brand_ids', item.toString());
      });

      const data: AxiosResponse<IChangeCompetition.Response> = await mainServiceWithCredentials.patch(
        `competitions/admin/competition/${form.id}/`,
        body,
      );

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2CRApi.util.invalidateTags(['allCompetitionsB2CR']));
      toast.success('Соревнование изменено');

      return data.data;
    } catch (e: any) {
      return handleErrorThunk(e, thunkAPI as RootThunkApiWithReject);
    }
  },
);

const updateCompetitionStatusThunk = createAsyncThunk(
  'competitionsApi/updateCompetitionStatusThunk',
  async ({ competition_id, status }: MChangeCompetitionStatus.Params, thunkAPI) => {
    try {
      await mainServiceWithCredentials.patch(`competitions/admin/competition/${competition_id}/status`, {
        data: status,
      });

      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2CRApi.util.invalidateTags(['allCompetitionsB2CR', 'competitionB2CR']));

      toast.success('Статус успешно изменен');
    } catch (e) {
      toast.error('При изменении статуса произошла ошибка');
      return handleErrorThunk(e, thunkAPI as RootThunkApiWithReject);
    }
  },
);

const addParticipantsToCompetitionsThunk = createAsyncThunk(
  'competitionsApi/addParticipantsToCompetitionThunk',
  async (form: IAddParticipantsToCompetitions.Params, thunkAPI) => {
    try {
      const body = new FormData();

      (Object.keys(form) as Array<keyof IAddParticipantsToCompetitions.Params>).forEach(
        // @ts-ignore
        (key) => form[key] !== undefined && body.append(key, form[key]),
      );

      const data: AxiosResponse<IAddParticipantsToCompetitions.Response> = await mainServiceWithCredentials.post(
        `competitions/admin/competition/${form.competition_id}/participant-xlsx`,
        body,
      );

      if (data.data.warnings && data.data.warnings.length > 0) {
        toast.warning(data.data.warnings[0].warning);
      } else {
        toast.success(`Назначено ${data.data.new_participants_count} новых участников`);
      }
      thunkAPI.dispatch(setDrawerOpen(false));
      thunkAPI.dispatch(competitionsB2CRApi.util.invalidateTags(['competitionB2CRParticipants']));

      return data.data;
    } catch (e: any) {
      return handleErrorThunk(e, thunkAPI as RootThunkApiWithReject, true);
    }
  },
);

export {
  createNewCompetitionThunk,
  updateCompetitionThunk,
  updateCompetitionStatusThunk,
  addParticipantsToCompetitionsThunk,
};
